<template>
  <li class="nav-item dropdown language-switch">
    <a
      href="#"
      class="nav-link dropdown-toggle"
      role="button"
      ref="dropdownToggle"
      @click.prevent="toggleDropdown"
      @mouseenter="handleMouseEnter"
      @mouseleave="handleMouseLeave"
    >
      <img
        :src="getFlagImage(currentLanguage)"
        class="flag-icon"
        alt="selected language"
      />
    </a>
    <ul
      class="dropdown-menu dropdown-menu-end"
      ref="dropdownMenu"
      @mouseenter="handleMouseEnter"
      @mouseleave="handleMouseLeave"
    >
      <li v-for="lang in availableLanguages" :key="lang.code">
        <a
          href="#"
          class="dropdown-item d-flex align-items-center"
          :class="{ active: currentLanguage === lang.code }"
          @click.prevent="switchLanguage(lang.code)"
        >
          <img
            :src="getFlagImage(lang.code)"
            class="flag-icon me-2"
            :alt="lang.name"
          />
          <span>{{ lang.name }}</span>
        </a>
      </li>
    </ul>
  </li>
</template>

<script>
import { computed, ref, onMounted, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { Dropdown } from 'bootstrap';

import swFlag from '@/assets/images/flag-tanzania.jpg';
import enFlag from '@/assets/images/en.svg';

export default {
  name: 'LanguageDropdown',
  emits: ['dropdown-opened'],
  setup(props, { emit }) {
    const { locale, t } = useI18n();
    const store = useStore();
    const dropdownToggle = ref(null);
    const dropdownMenu = ref(null);
    let dropdownInstance = null;
    let hoverTimeout = null;

    const availableLanguages = computed(() => [
      { code: 'sw', name: t('languages.sw') },
      { code: 'en', name: t('languages.en') }
    ]);

    const flagImages = {
      en: enFlag,
      sw: swFlag
    };

    const currentLanguage = computed(() => locale.value);

    const toggleDropdown = (e) => {
      e.preventDefault();
      e.stopPropagation();
      
      if (!dropdownInstance) return;
      
      // Emit event to close other dropdowns
      emit('dropdown-opened');
      
      const isShown = dropdownMenu.value?.classList.contains('show');
      
      if (isShown) {
        dropdownInstance.hide();
      } else {
        dropdownInstance.show();
      }
    };

    const closeDropdown = () => {
      if (dropdownInstance) {
        dropdownInstance.hide();
      }
    };

    const switchLanguage = (langCode) => {
      locale.value = langCode;
      store.dispatch('changeLanguage', langCode);
      closeDropdown();
    };

    const getFlagImage = (code) => flagImages[code];

    const handleMouseEnter = () => {
      if (window.innerWidth >= 769) {
        clearTimeout(hoverTimeout);
        if (dropdownInstance) {
          // Emit event to close account dropdown
          emit('dropdown-opened');
          dropdownInstance.show();
        }
      }
    };

    const handleMouseLeave = () => {
      if (window.innerWidth >= 769) {
        hoverTimeout = setTimeout(() => {
          if (dropdownInstance) {
            dropdownInstance.hide();
          }
        }, 200);
      }
    };

    // Close dropdown when clicking outside
    const handleClickOutside = (event) => {
      const isDropdown = dropdownToggle.value?.contains(event.target) || 
                         dropdownMenu.value?.contains(event.target);
      
      if (!isDropdown) {
        closeDropdown();
      }
    };

    onMounted(() => {
      if (dropdownToggle.value) {
        dropdownInstance = new Dropdown(dropdownToggle.value, {
          autoClose: true
        });
        
        // Handle touch events for mobile
        dropdownToggle.value.addEventListener('touchstart', (e) => {
          e.preventDefault();
          toggleDropdown(e);
        });
      }

      // Listen for close-language-dropdown event
      document.addEventListener('close-language-dropdown', closeDropdown);
      
      // Click outside handler
      document.addEventListener('click', handleClickOutside);
      
      // Close dropdown on escape key
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
          closeDropdown();
        }
      });
    });

    onUnmounted(() => {
      if (dropdownInstance) {
        dropdownInstance.dispose();
      }
      if (hoverTimeout) {
        clearTimeout(hoverTimeout);
      }
      document.removeEventListener('close-language-dropdown', closeDropdown);
      document.removeEventListener('click', handleClickOutside);
    });

    return {
      availableLanguages,
      currentLanguage,
      switchLanguage,
      getFlagImage,
      dropdownToggle,
      dropdownMenu,
      toggleDropdown,
      closeDropdown,
      handleMouseEnter,
      handleMouseLeave
    };
  }
};
</script>

<style scoped>
.flag-icon {
  width: 24px;
  height: 18px;
  object-fit: cover;
  border-radius: 3px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
}

.language-switch .dropdown-item.active {
  background-color: #f0f0f0;
  font-weight: bold;
}

/* Mobile-friendly dropdown styles */
@media (max-width: 768px) {
  .language-switch .dropdown-menu {
    position: absolute;
    right: 0;
    left: auto;
    margin-top: 0.5rem;
  }
  
  .dropdown-toggle::after {
    display: inline-block;
    margin-left: 0.255em;
    vertical-align: 0.255em;
    content: "";
    border-top: 0.3em solid;
    border-right: 0.3em solid transparent;
    border-bottom: 0;
    border-left: 0.3em solid transparent;
  }
}

/* Disable hover on mobile */
@media (max-width: 768px) {
  .language-switch:hover .dropdown-menu {
    display: none;
  }
}
</style>